/**
 * Phase 8 · C-13 — Library sources read service.
 *
 * Reads `ggj_b2b_library_sources` (migration 099). Read-only surface for the
 * LibraryPage TanStack Query consumer; writes (connect / disconnect / CSV
 * upload) land in a later ticket.
 *
 * Fails gracefully — on any error the caller surfaces a retry-able UI state
 * rather than crashing. This matches the ticket's "don't block on the
 * migration being applied to remote Supabase" note.
 */
import { supabase } from '../client';

export type LibrarySourceKind = 'linkedin' | 'naukri' | 'internal' | 'csv' | 'api';
export type LibrarySourceStatus = 'active' | 'warn' | 'disconnected';

export interface LibrarySource {
  id: string;
  orgId: string;
  name: string;
  kind: LibrarySourceKind;
  description: string | null;
  itemCount: number;
  iconKey: string | null;
  status: LibrarySourceStatus;
  createdAt: string;
  updatedAt: string;
}

interface LibrarySourceRow {
  id: string;
  org_id: string;
  name: string;
  kind: string;
  description: string | null;
  item_count: number | null;
  icon_key: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
}

function normaliseKind(k: string | null | undefined): LibrarySourceKind {
  if (k === 'linkedin' || k === 'naukri' || k === 'internal' || k === 'csv' || k === 'api') return k;
  return 'api';
}

function normaliseStatus(s: string | null | undefined): LibrarySourceStatus {
  if (s === 'active' || s === 'warn' || s === 'disconnected') return s;
  return 'active';
}

function mapRow(r: LibrarySourceRow): LibrarySource {
  return {
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    kind: normaliseKind(r.kind),
    description: r.description,
    itemCount: Math.max(0, r.item_count ?? 0),
    iconKey: r.icon_key,
    status: normaliseStatus(r.status),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export const librarySourceService = {
  /**
   * List all sources for an org, newest first.
   *
   * Throws on network / RLS errors so TanStack Query surfaces `isError` and
   * LibraryPage can render the "Could not load sources · Retry" state.
   */
  async list(orgId: string): Promise<LibrarySource[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_library_sources')
      .select('id, org_id, name, kind, description, item_count, icon_key, status, created_at, updated_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => mapRow(row as LibrarySourceRow));
  },
};
